/**
 * Uncaught exceptions thrown by scripts the page never loaded — browser extensions and
 * the inline or eval'd snippets they inject — reach `window.onerror` with our own frames
 * underneath them, because their listeners run during events we dispatch. That makes them
 * look like application errors in the console and in RUM.
 *
 * Classification always keys off the frame that actually threw, never the rest of the
 * stack, so an error originating in first-party code can never be treated as third-party.
 */

/**
 * A script location that cannot belong to us. Chrome renders a frame with no script URL
 * as `<anonymous>`; Datadog's stack formatter renders the same frame as `undefined`.
 */
const NON_APP_LOCATION = /^(?:undefined(?::|$)|<anonymous>|eval\b|native\b|[\w-]+-extension:\/\/)/;

/**
 * Extracts the script location from a stack frame. Handles V8 (`at fn (loc)` and
 * `at loc`), Datadog's formatter (`at fn @ loc`) and SpiderMonkey (`fn@loc`).
 */
const frameLocation = (frame: string): string => {
    const parenthesised = frame.match(/\(([^()]*)\)\s*$/);
    if (parenthesised) {
        return parenthesised[1].trim();
    }

    const datadogFormatted = frame.match(/^\s*at\s+.*?\s+@\s*(.*)$/);
    if (datadogFormatted) {
        return datadogFormatted[1].trim();
    }

    const spiderMonkey = frame.match(/^\s*[\w$.<>[\]]*@(.*)$/);
    if (spiderMonkey) {
        return spiderMonkey[1].trim();
    }

    return frame.replace(/^\s*at\s+/, '').trim();
};

const findThrowingFrame = (stack: string): string | undefined => {
    const lines = stack.split('\n');
    // Prefer V8 frames (`at fn (loc)`); fall back to SpiderMonkey (`fn@loc`), whose stacks
    return lines.find((line) => /^\s*at\s/.test(line)) ?? lines.find((line) => /^\s*[\w$.<>[\]]*@\S/.test(line));
};

/**
 * Whether the topmost frame of `stack` belongs to a script we never loaded. Returns false
 * when there is no usable stack, so an unattributable error is always kept.
 */
export const isThrownByNonAppScript = (stack: string | undefined): boolean => {
    if (!stack) {
        return false;
    }

    const throwingFrame = findThrowingFrame(stack);
    return !!throwingFrame && NON_APP_LOCATION.test(frameLocation(throwingFrame));
};

/**
 * Stops third-party script errors from being reported to the console.
 *
 * `preventDefault` cancels only the browser's default reporting of the exception. There is
 * deliberately no `stopImmediatePropagation` here: every other error handler still runs,
 * so error trackers keep receiving the event and decide for themselves (RUM drops it in
 * `dataDogBeforeSend`, using this same predicate).
 */
export const silenceNonAppScriptErrors = (): (() => void) => {
    const handleError = (event: ErrorEvent) => {
        if (isThrownByNonAppScript(event.error?.stack)) {
            event.preventDefault();
        }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
};
